import React, { useState } from 'react';
import { Typography, Card, Input, Select, Button, Badge } from 'antd';
import { ArrowLeftOutlined, SearchOutlined, ShopOutlined, UserOutlined, TeamOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import './CreateCampaign.css';

const { Title, Text } = Typography;
const { Option } = Select;

const CreateCampaign = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Mock clients data
  const clients = [
    {
      id: 1,
      name: 'ProShine Auto Detailing',
      contact: 'Maria Santos',
      locations: 1,
      vehicles: 5,
      mrr: '$733/mo',
      status: 'active'
    },
    {
      id: 2,
      name: 'Blue Ridge HVAC',
      contact: 'James Mitchell',
      locations: 3,
      vehicles: 18,
      mrr: '$2,206/mo',
      status: 'active'
    }
  ];

  // Mock locations for Blue Ridge HVAC (client 2)
  const blueRidgeLocations = [
    { id: 1, name: 'Asheville HQ', address: '100 Tunnel Rd', city: 'Asheville, NC' },
    { id: 2, name: 'Greenville Branch', address: '200 Pleasantburg Dr', city: 'Greenville, SC' },
    { id: 3, name: 'Johnson City Office', address: '300 State of Franklin', city: 'Johnson City, TN' }
  ];

  const handleClientSelect = (client) => {
    setSelectedClient(client);
    setCurrentStep(2);
  };

  const handleLocationToggle = (locationId) => {
    setSelectedLocations(prev => 
      prev.includes(locationId)
        ? prev.filter(id => id !== locationId)
        : [...prev, locationId]
    );
  };

  const handleContinueToType = () => {
    if (selectedLocations.length > 0) {
      setCurrentStep(3);
    }
  };

  const handleCampaignTypeSelect = (type) => {
    // Navigate to existing campaign flow with pre-selected data
    navigate('/client/campaigns/new-campaign-ux', {
      state: {
        clientId: selectedClient.id,
        locationIds: selectedLocations,
        campaignType: type
      }
    });
  };

  const filteredClients = clients.filter(client => {
    const matchesSearch = searchTerm === '' || 
      client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      client.contact.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || client.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const renderStepContent = () => {
    if (currentStep === 1) {
      return (
        <div className="step-content">
          <div className="step-header-center">
            <Title level={2}>Select Client</Title>
            <Text type="secondary">Choose a client to create a campaign for</Text>
          </div>

          <div className="filters">
            <Input
              placeholder="Search clients..."
              prefix={<SearchOutlined />}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ width: 300 }}
            />
            <Select
              value={statusFilter}
              onChange={setStatusFilter}
              style={{ width: 150 }}
            >
              <Option value="all">All Status</Option>
              <Option value="active">Active</Option>
              <Option value="inactive">Inactive</Option>
            </Select>
          </div>

          <div className="client-cards">
            {filteredClients.map(client => (
              <Card
                key={client.id}
                className="client-card-select"
                onClick={() => handleClientSelect(client)}
                hoverable
              >
                <div className="client-card-header">
                  <div className="client-icon">
                    <ShopOutlined />
                  </div>
                  <div className="client-info">
                    <Title level={4} style={{ margin: 0 }}>{client.name}</Title>
                    <Text type="secondary">{client.contact}</Text>
                  </div>
                  <Badge status="success" text="active" />
                </div>
                <div className="client-stats-row">
                  <div>
                    <Text type="secondary">Locations</Text>
                    <div className="stat-num">{client.locations}</div>
                  </div>
                  <div>
                    <Text type="secondary">Vehicles</Text>
                    <div className="stat-num">{client.vehicles}</div>
                  </div>
                </div>
              </Card>
            ))}
          </div>

          <div className="step-footer">
            <Text type="secondary">{filteredClients.length} clients available</Text>
          </div>
        </div>
      );
    }

    if (currentStep === 2) {
      return (
        <div className="step-content">
          <div className="step-header-center">
            <div className="client-badge">
              <ShopOutlined /> {selectedClient.name}
            </div>
            <Title level={2}>Select Locations</Title>
            <Text type="secondary">Choose one or more business locations for this campaign</Text>
          </div>

          <div className="filters">
            <Input
              placeholder="Search locations..."
              prefix={<SearchOutlined />}
              style={{ width: 300 }}
            />
            <Select defaultValue="all" style={{ width: 150 }}>
              <Option value="all">All States</Option>
              <Option value="nc">North Carolina</Option>
              <Option value="sc">South Carolina</Option>
              <Option value="tn">Tennessee</Option>
            </Select>
            <Select defaultValue="all" style={{ width: 150 }}>
              <Option value="all">All Cities</Option>
            </Select>
            <Button type="link" onClick={() => setSelectedLocations(blueRidgeLocations.map(l => l.id))}>
              Select All ({blueRidgeLocations.length})
            </Button>
          </div>

          <div className="location-cards">
            {blueRidgeLocations.map(location => (
              <Card
                key={location.id}
                className={`location-card ${selectedLocations.includes(location.id) ? 'selected' : ''}`}
                onClick={() => handleLocationToggle(location.id)}
                hoverable
              >
                <div className="location-icon">
                  <TeamOutlined />
                </div>
                <div className="location-info">
                  <Title level={5} style={{ margin: 0 }}>{location.name}</Title>
                  <Text type="secondary">{location.address}</Text>
                  <br />
                  <Text type="secondary">{location.city}</Text>
                </div>
              </Card>
            ))}
          </div>

          <div className="step-footer">
            <Text type="secondary">{blueRidgeLocations.length} locations available</Text>
            <Button
              type="primary"
              size="large"
              onClick={handleContinueToType}
              disabled={selectedLocations.length === 0}
            >
              Continue
            </Button>
          </div>
        </div>
      );
    }

    if (currentStep === 3) {
      return (
        <div className="step-content">
          <div className="step-header-center">
            <Title level={2}>Choose Campaign Type</Title>
            <Text type="secondary">Select how you want to reach customers</Text>
          </div>

          <div className="campaign-type-cards">
            <Card
              className="campaign-type-card"
              onClick={() => handleCampaignTypeSelect('proximity')}
              hoverable
            >
              <div className="type-badge">Most Popular</div>
              <div className="type-icon">
                <UserOutlined />
              </div>
              <Title level={4}>ProximityIQ + CRM</Title>
              <Text type="secondary">Reach customers when you're nearby</Text>
              <div className="type-description">
                <Text>Send personalized emails and texts to CRM contacts when your fleet enters their neighborhood.</Text>
              </div>
            </Card>

            <Card
              className="campaign-type-card"
              onClick={() => handleCampaignTypeSelect('route')}
              hoverable
            >
              <div className="type-icon">
                <TeamOutlined />
              </div>
              <Title level={4}>RouteLogic™</Title>
              <Text type="secondary">Turn every mile into a marketing opportunity</Text>
              <div className="type-description">
                <Text>Create digital breadcrumbs along your service routes to target homeowners you pass.</Text>
              </div>
            </Card>

            <Card
              className="campaign-type-card"
              onClick={() => handleCampaignTypeSelect('stop')}
              hoverable
            >
              <div className="type-icon">
                <ShopOutlined />
              </div>
              <Title level={4}>StopLogic™</Title>
              <Text type="secondary">Target where you've already worked</Text>
              <div className="type-description">
                <Text>Use job stop locations to reach nearby homeowners in neighborhoods you've serviced.</Text>
              </div>
            </Card>
          </div>

          <div className="step-footer">
            <Text type="secondary">3 campaign types available</Text>
          </div>
        </div>
      );
    }
  };

  return (
    <div className="create-campaign-page">
      {/* Header */}
      <div className="create-campaign-header">
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => currentStep === 1 ? navigate('/whitelabel/campaigns') : setCurrentStep(currentStep - 1)}
        >
          {currentStep === 1 ? 'Back to Admin' : 'Back'}
        </Button>
        <div className="step-indicator">Step {currentStep} of 4</div>
      </div>

      {renderStepContent()}
    </div>
  );
};

export default CreateCampaign;
